import {
  FLASH_SALE_CTA_LABEL,
  FLASH_SALE_EYEBROW,
  FLASH_SALE_HEADLINE,
  FLASH_SALE_SUBTITLE,
  getFlashSaleCheckoutHref,
} from '~/features/promotions/constants';
import type { FlashTier } from '~/features/promotions/utils/get-flash-tier';
import { BUILD_AI_AGENTS_WEBINAR_REGISTRATION_HREF } from '~/utils/build-ai-agents-webinar-atf-slot';

export type ExperienceAtfCardAction = { type: 'link'; href: string } | { type: 'video'; videoId: string };

/**
 * Matches ATF promo templates in Figma (node 7374:9689 + 7633:25654 + 7665:32868).
 * The live flash card uses the `jun_2026_flash` variant.
 */
export type ExperienceAtfPromoVariant =
  | 'agentic_ai'
  | 'build_ai_agents_webinar'
  | 'free'
  | 'jun_2026_flash'
  | 'marty_cagan_webinar'
  | 'mba';

export type ExperienceAtfCardConfig = {
  id: string;
  action: ExperienceAtfCardAction;
  ctaLabel: string;
  description: string;
  eyebrow: string;
  promoVariant: ExperienceAtfPromoVariant;
  /** Primary heading (H2 / tests). */
  title: string;
  /** Optional second line (Agentic card). */
  titleLine2?: string;
};

/** Static art for ATF promos (`public/images/experiments/ab_experience_atf_2026/`). */
export const ATF_PROMO_ASSETS = {
  juneFlashPromoBg: '/images/experiments/ab_experience_atf_2026/jun-flash-promo-bg.jpg',
  agenticAiBg: '/images/experiments/ab_experience_atf_2026/agentic-ai-bg-v2.png',
  buildAiAgentsWebinarPortrait:
    '/images/experiments/ab_experience_atf_2026/peter-kowalchuk-build-ai-agents-webinar.jpg',
  martyCaganPortrait: '/images/experiments/ab_experience_atf_2026/marty-cagan-webinar-portrait.png',
  martyCaganWaveBg: '/images/experiments/ab_experience_atf_2026/marty-cagan-webinar-wave-bg.png',
  mbaBg: '/images/experiments/ab_experience_atf_2026/mba-bg.png',
  schoolsGrid: '/images/experiments/ab_experience_atf_2026/schools-grid.png',
} as const;

/**
 * Intrinsic pixel size of each file (must match what is on disk).
 * MBA art is exported @2x for a 300×375 CSS card; schools grid is still @1x until re-exported.
 * Flash promo bg is also @2x (600×750 for a 300×375 shell).
 */
export const ATF_PROMO_ASSET_INTRINSIC_PX = {
  juneFlashPromoBg: { height: 750, width: 600 },
  buildAiAgentsWebinarPortrait: { height: 280, width: 232 },
  martyCaganPortrait: { height: 280, width: 232 },
  martyCaganWaveBg: { height: 750, width: 600 },
  mbaBg: { height: 750, width: 600 },
  schoolsGrid: { height: 158, width: 205 },
} as const;

/** Desktop promo shell (Figma 7374:9689). */
export const EXPERIENCE_ATF_CARD_SHELL_WIDTH_PX = 300;
export const EXPERIENCE_ATF_CARD_SHELL_HEIGHT_PX = 375;

/**
 * Mobile promo shell (Figma 7382:11004 — node 7382:11940 frame).
 * Outer slide width keeps a 5px inset each side, same as desktop.
 */
export const EXPERIENCE_ATF_CARD_SHELL_WIDTH_PX_MOBILE = 260;
export const EXPERIENCE_ATF_CARD_SHELL_HEIGHT_PX_MOBILE = 325;

/** Swiper slide / static SSR row cell width (px); matches card outer width. */
export const EXPERIENCE_ATF_SLIDE_WIDTH_PX = 310;
export const EXPERIENCE_ATF_SLIDE_WIDTH_PX_MOBILE = 270;

/**
 * Min height for the carousel row (avoids CLS). Shell height + vertical inset (desktop 375+13, mobile 325+13).
 */
export const EXPERIENCE_ATF_CAROUSEL_MIN_HEIGHT_PX = 388;
export const EXPERIENCE_ATF_CAROUSEL_MIN_HEIGHT_PX_MOBILE = 338;

/**
 * Marty Cagan replay — default slot 4 on /experience ATF (Figma 7665:32868).
 */
export const EXPERIENCE_ATF_MARTY_CAGAN_WEBINAR_CARD: ExperienceAtfCardConfig = {
  id: 'marty_cagan_webinar_apr2026',
  promoVariant: 'marty_cagan_webinar',
  eyebrow: 'webinar',
  title: 'The new standard for PMs with Marty Cagan',
  description: 'Build, lead, and grow products in the age of AI',
  ctaLabel: 'Watch Now',
  action: { type: 'link', href: '/video/new-standard-for-product-managers-replay' },
};

/**
 * Build AI Agents live webinar — replaces slot 4 while `isBuildAiAgentsWebinarAtfSlotActive` is true
 * (Figma 888:27846, CONENG-2426).
 */
export const EXPERIENCE_ATF_BUILD_AI_AGENTS_WEBINAR_CARD: ExperienceAtfCardConfig = {
  id: 'build_ai_agents_webinar_jun2026',
  promoVariant: 'build_ai_agents_webinar',
  eyebrow: 'webinar',
  title: 'Live build: AI agents',
  titleLine2: 'from scratch',
  description: 'How AI agents work under the hood',
  ctaLabel: 'Register For Free!',
  action: { type: 'link', href: BUILD_AI_AGENTS_WEBINAR_REGISTRATION_HREF },
};

/**
 * Promo order (agentic → free → mba → marty_cagan_webinar). Agentic leads by default after
 * the free-trial experiment ended.
 */
export const EXPERIENCE_ATF_CARDS: ExperienceAtfCardConfig[] = [
  {
    /**
     * Default-path Agentic AI promo. After the `agentic-ai-ft-2026` free-trial experiment
     * ended, the free-trial visual shell was promoted as the OG `agentic` look (palette,
     * decoration, rocket CTA chrome); flag-on traffic still prepends `FREE_TRIAL_ATF_CARD`
     * but reuses the same OG variant + `ATF_agentic` analytics.
     */
    id: 'agentic_ai',
    promoVariant: 'agentic_ai',
    eyebrow: 'Most popular',
    title: 'Build AI systems',
    titleLine2: 'that act.',
    description: 'Every agentic AI skill, in one place.',
    ctaLabel: 'Enroll Now',
    action: { type: 'link', href: '/course/agentic-ai--nd900' },
  },
  {
    id: 'free_courses',
    promoVariant: 'free',
    eyebrow: 'Free programs',
    title: 'Start building AI skills for free',
    description: 'Explore top-rated courses and start today.',
    ctaLabel: 'Start Free Today',
    action: { type: 'link', href: '/catalog?price=free' },
  },
  {
    id: 'mba_ai',
    promoVariant: 'mba',
    eyebrow: 'New at Udacity',
    title: 'Level up with an MBA',
    description: 'Earn a real MBA credential designed for AI product leaders.',
    ctaLabel: 'Explore MBA in AI',
    action: { type: 'link', href: '/mba-ai-product-management' },
  },
  EXPERIENCE_ATF_MARTY_CAGAN_WEBINAR_CARD,
];

/**
 * First-position promo tile shown on the `agentic-ai-ft-2026=on` flag-on /experience page
 * (Figma 115:22190). Shares the OG `agentic` visual + analytics now that the free-trial
 * design was promoted to the default Agentic tile.
 */
export const FREE_TRIAL_ATF_CARD: ExperienceAtfCardConfig = {
  id: 'agentic_ai_free_trial',
  promoVariant: 'agentic_ai',
  eyebrow: 'Most popular',
  title: 'Build AI systems',
  titleLine2: 'that act.',
  description: 'Every agentic AI skill, in one place.',
  ctaLabel: 'Try For Free',
  action: {
    type: 'link',
    href: '/course/agentic-ai--nd900',
  },
};

export const EXPERIMENT_KEY = 'ab_experience_atf_2026';

/**
 * First-position promo tile shown when a June flash-sale promotion is active. Gated on the
 * promotion `promotionId` (`48JUNE48` / `48JUNE10`); the headline differs by market tier while the
 * shell, subtitle, CTA, and checkout deep link are shared (Figma 796:31724 / 796:31867). The live
 * `promoCode` is appended to the checkout link as `?coupon=` (fallback for coupon auto-apply).
 */
export function getExperienceAtfFlashCard(tier: FlashTier, promoCode?: string | null): ExperienceAtfCardConfig {
  return {
    id: 'jun_2026_flash',
    promoVariant: 'jun_2026_flash',
    eyebrow: FLASH_SALE_EYEBROW,
    title: FLASH_SALE_HEADLINE[tier],
    description: FLASH_SALE_SUBTITLE,
    ctaLabel: FLASH_SALE_CTA_LABEL,
    action: {
      type: 'link',
      href: getFlashSaleCheckoutHref(tier, promoCode),
    },
  };
}
